import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/lib/api';
import BrandSymbol from '@/components/BrandSymbol';
import PlanComparison from '@/components/PlanComparison';
import PageDetailsAccordion from '@/components/PageDetailsAccordion';
import { EditorialPage, DataMessage } from '@/components/Editorial';

const primary = 'inline-flex items-center justify-center rounded-full bg-[#557448] text-white px-5 py-3 text-sm font-medium hover:bg-[#46633b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#557448]';
const secondary = 'text-[#557448] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4';
const Section = ({ title, id, children }) => <section id={id} className="mt-8 scroll-mt-24 min-w-0"><h2 className="font-serif text-2xl sm:text-3xl text-[#2A2521] mb-4">{title}</h2>{children}</section>;
const Bullets = ({ items }) => <ul className="list-disc pl-5 space-y-2 leading-relaxed text-[#4A443D]">{items.map(item => <li key={item}>{item}</li>)}</ul>;

export default function Plans() {
  const { lang, t } = useLang();
  const { user, isChecking } = useAuth();
  const [commercial, setCommercial] = useState(null), [commercialError, setCommercialError] = useState(false);
  const [profiles, setProfiles] = useState({ ownerId: null, rows: null, error: false });
  const [attempt, setAttempt] = useState(0);
  const ownerId = user?.id || null;
  const checking = isChecking || user === null;

  useEffect(() => {
    const controller = new AbortController();
    setCommercial(null); setCommercialError(false);
    Promise.all([
      api.get('/plans', { signal: controller.signal }),
      api.get('/billing/catalog', { signal: controller.signal }),
    ]).then(([plans, billing]) => {
      if (controller.signal.aborted) return;
      const annual = billing.data.plans?.find(p => p.plan === 'pro' && p.interval === 'year');
      const entitlements = plans.data.entitlements;
      const fees = billing.data.commission_basis_points;
      if (!annual || !Number.isInteger(annual.amount_cents) || annual.currency !== 'eur' ||
          !Number.isInteger(entitlements?.free?.max_products) || !Number.isInteger(entitlements?.pro?.max_products) ||
          !Number.isInteger(entitlements?.pro?.max_opportunities) || !Number.isInteger(fees?.pro) ||
          !Number.isInteger(fees?.free) || !Number.isInteger(billing.data.sponsorship_basis_points) ||
          !plans.data.free_project_categories?.includes('public_body')) throw new Error('Incomplete commercial information');
      setCommercial({ annual, entitlements, fees, sponsorship: billing.data.sponsorship_basis_points });
    }).catch(() => { if (!controller.signal.aborted) setCommercialError(true); });
    return () => controller.abort();
  }, [attempt]);

  useEffect(() => {
    if (!ownerId) return;
    const controller = new AbortController();
    setProfiles({ ownerId, rows: null, error: false });
    api.get('/my/farms', { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      // Administrators can see other owners' profiles; billing remains owner-specific.
      if (!Array.isArray(data)) throw new Error('Invalid profile response');
      setProfiles({ ownerId, rows: data.filter(f => f.owner_id === ownerId && !f.archived), error: false });
    }).catch(() => { if (!controller.signal.aborted) setProfiles({ ownerId, rows: null, error: true }); });
    return () => controller.abort();
  }, [ownerId]);

  const number = value => new Intl.NumberFormat(lang === 'it' ? 'it-IT' : 'en-GB', { maximumFractionDigits: 2 }).format(value);
  const price = commercial ? new Intl.NumberFormat(lang === 'it' ? 'it-IT' : 'en-GB', { style: 'currency', currency: commercial.annual.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(commercial.annual.amount_cents / 100) : '…';
  const annualPrice = t('plansPage.annualPrice').replace('{price}', price);
  const percent = bps => number(bps / 100) + '%';
  const currentProfiles = profiles.ownerId === ownerId ? profiles : { rows: null, error: false };
  let action = null;
  if (!checking && !user) action = { to: '/register', label: t('plansPage.startFree') };
  else if (ownerId && currentProfiles.error) action = { to: '/dashboard', label: t('plansPage.dashboard') };
  else if (ownerId && currentProfiles.rows) {
    if (!currentProfiles.rows.length) action = { to: '/list-your-farm', label: t('plansPage.present') };
    else if (currentProfiles.rows.length > 1) action = { to: '/dashboard', label: t('plansPage.chooseProfile') };
    else {
      const profile = currentProfiles.rows[0];
      const sub = profile.subscription || {};
      const paid = Boolean(sub.provider_subscription_id) && !['canceled', 'cancelled', 'incomplete_expired'].includes(sub.status) && !sub.complimentary;
      action = { to: `/dashboard/farms/${encodeURIComponent(profile.id)}/billing`, label: t(paid ? 'plansPage.manageSubscription' : 'plansPage.managePlan') };
    }
  }
  const actionLink = action ? <Link className={primary} to={action.to} data-testid="plans-primary-cta">{action.label}</Link> : <p role="status" className="text-sm">{t('plansPage.checkingAccount')}</p>;
  const proPoints = t('plansPage.proPoints').map(s => s.replace('{products}', commercial ? number(commercial.entitlements.pro.max_products) : '…').replace('{opportunities}', commercial ? number(commercial.entitlements.pro.max_opportunities) : '…'));

  return <EditorialPage testId="plans-page" title={t('plansPage.title')} intro={t('plansPage.intro')}>
    <p className="mt-3 text-[#4A443D]">{t('plansPage.personalAccount')}</p>
    <p className="mt-1 text-sm text-[#6E6258]">{t('plansPage.offersNote')}</p>
    <section className="mt-6" aria-label={t('plansPage.cardsTitle')}>
      <div className="grid md:grid-cols-2 gap-5" data-testid="plans-summary-cards">
        <article className="min-w-0 rounded-md border border-[#E2DACB] bg-[#FFFDF8] p-5 sm:p-6">
          <h2 className="flex items-center gap-3 font-serif text-3xl"><BrandSymbol kind="leaf" size={48} />Free</h2>
          <p className="mt-3 mb-4 text-[#6E6258]">{t('plansPage.freeTagline')}</p>
          <Bullets items={t('plansPage.freePoints')} />
          {!checking && !user && <Link to="/register" className={`${primary} mt-5`}>{t('plansPage.startFree')}</Link>}
        </article>
        <article className="min-w-0 rounded-md border border-[#E2DACB] bg-[#FFFDF8] p-5 sm:p-6">
          <h2 className="flex items-center gap-3 font-serif text-3xl"><BrandSymbol kind="crown" size={48} />Pro</h2>
          <p className="mt-3 text-lg font-semibold text-[#557448]" data-testid="plans-annual-price">{annualPrice}</p>
          <p className="mt-2 mb-4 text-[#6E6258]">{t('plansPage.proTagline')}</p>
          <Bullets items={proPoints} />
          <p className="text-sm text-[#6E6258] mt-4">{t('plansPage.renewalShort')}</p>
          <Link to="#pro-subscription" className={`${secondary} inline-block mt-4`}>{t('plansPage.detailsTitle')}</Link>
        </article>
      </div>
      <p className="text-sm text-[#6E6258] mt-3">{t('plansPage.optionalData')}</p>
      <p className="rounded-md border border-[#E2DACB] bg-[#EAF0E6] p-4 mt-4 text-[#3E5733]" data-testid="plans-public-exception">{t('plansPage.publicException')}</p>
    </section>
    <Section title={t('plansPage.comparisonTitle')}>
      {!commercial ? <DataMessage error={commercialError} onRetry={commercialError ? () => setAttempt(a => a + 1) : undefined} retryLabel={t('plansPage.retry')}>{t(commercialError ? 'plansPage.commercialError' : 'plansPage.loading')}</DataMessage> : <>
        <p className="text-sm text-[#6E6258] mb-3" id="plans-table-help">{t('plansPage.scrollTable')}</p>
        <PlanComparison hideTitle presentation="plans" commercial={{ ...commercial, annualPrice }} />
      </>}
    </Section>
    <Section title={t('plansPage.detailsTitle')}>
      <PageDetailsAccordion panels={[
        { id: 'pro-subscription', aliases: ['attivazione'], title: t('plansPage.subscriptionDetailsTitle'), children: <>
          <p>{t('plansPage.activation')}</p>
          <p>{t('plansPage.activationNote')}</p>
          <Bullets items={t('plansPage.subscriptionDetails')} />
          <p className="text-sm"><Link className={secondary} to="/subscription-terms">{t('plansPage.terms')}</Link>. {t('plansPage.refundNote')}</p>
        </> },
        { id: 'pro-payments-fees', title: t('plansPage.paymentsDetailsTitle'), children: <>
          <p>{t('plansPage.receivingPayments')}</p>
          {/* Zero fees follow billing.commission's default branch for symbolic support and contributions. */}
          {commercial ? <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="plans-fees">{[commercial.fees.pro, commercial.sponsorship, 0, 0].map((bps, i) => <div key={i} className="flex flex-wrap justify-between gap-2 border-b border-[#E2DACB] pb-2"><dt>{t('plansPage.feesLabels')[i]}</dt><dd className="font-semibold text-[#557448]">{percent(bps)}</dd></div>)}</dl> : <DataMessage error={commercialError} onRetry={commercialError ? () => setAttempt(a => a + 1) : undefined} retryLabel={t('plansPage.retry')}>{t(commercialError ? 'plansPage.commercialError' : 'plansPage.loading')}</DataMessage>}
          <p>{t('plansPage.feesNote')}</p>
          {commercial && <p className="text-sm">{t('plansPage.legacyFee').replace('{fee}', percent(commercial.fees.free))}</p>}
          <p className="text-sm"><Link className={secondary} to="/subscription-terms">{t('plansPage.terms')}</Link></p>
        </> },
        { id: 'pro-contributions', title: t('plansPage.contributionsDetailsTitle'), children: <Bullets items={t('plansPage.contributionPoints')} /> },
      ]} />
    </Section>
    <Section title={t('plansPage.startTitle')}>
      {ownerId && currentProfiles.error && <p role="alert" className="text-sm mb-3">{t('plansPage.profilesError')}</p>}
      {actionLink}
      <nav className="flex flex-wrap gap-x-5 gap-y-2 mt-5 text-sm" aria-label={t('plansPage.startTitle')}>
        <Link className={secondary} to="/how-it-works">{t('plansPage.formulas')}</Link>
        <Link className={secondary} to="/subscription-terms">{t('plansPage.terms')}</Link>
        <Link className={secondary} to="/faq">{t('plansPage.faq')}</Link>
      </nav>
    </Section>
  </EditorialPage>;
}
